import type { MeResponse } from '@epc/contracts';
import { ApiError, api } from '../../../lib/api';
import type { OfflineScope } from '../offline/store';
import { type ReviewCommand, validateDecisionReceipt } from './model';
import type { ReviewRecord } from './store';

type Request = typeof api;
/** Только явная онлайн-попытка. Таймаут не создаёт новую команду и не разрешает другое решение. */
export async function sendReviewDecision(
  row: ReviewRecord,
  scope: OfflineScope,
  request: Request = api,
): Promise<ReviewCommand> {
  const command = row.command;
  if (!command || !['pending', 'auth_paused'].includes(command.state))
    throw new Error('Нет решения для повторения');
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  const result = (state: ReviewCommand['state'], error: string | null): ReviewCommand => ({
    ...command,
    state,
    error,
  });
  try {
    const me = await request<MeResponse>('/auth/me', { signal: controller.signal });
    if (me.user.id !== scope.userId || me.tenant?.id !== scope.tenantId || !me.tenant.staff)
      return result('auth_paused', 'Вернитесь в исходную учётную запись и компанию');
    const response = await request(
      `/site/reports/${row.context.report.id}/${command.action === 'accept' ? 'accept' : 'review'}`,
      {
        method: 'POST',
        body: JSON.parse(command.requestJson),
        idempotencyKey: command.key,
        expectedIdentity: { userId: scope.userId, tenantId: scope.tenantId },
        signal: controller.signal,
      },
    );
    const report = validateDecisionReceipt(
      response,
      row.context.report,
      command,
      scope,
      row.context.sourceRevisions[0]!.id,
    );
    return { ...command, state: 'done', error: null, result: report };
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 401 || error.code === 'session_context_changed' || error.code === 'view_as')
        return result('auth_paused', 'Сессия изменилась. Войдите под исходным пользователем');
      if (error.status === 409 && error.code === 'command_in_progress')
        return result('pending', 'Предыдущая попытка ещё выполняется. Повторите тот же запрос');
      if (error.status === 409 && ['revision_conflict', 'source_revision_conflict'].includes(error.code))
        return result('conflict', error.message);
      if (error.status >= 400 && error.status < 500 && error.status !== 429)
        return result('rejected', error.message);
    }
    return result(
      'pending',
      'Исход запроса пока неизвестен. Повторите то же решение, чтобы получить подтверждение',
    );
  } finally {
    clearTimeout(timeout);
  }
}
